/**
 * WhatsNewStrip — discovery surface for newly shipped features.
 *
 * Sits at the top of the CEO dashboard AND every division dashboard.  Shows
 * the 3 most-recent items from `WHATS_NEW` below — filtered by audience so
 * TDB/ME leaders don't see Concierge-only items they can't act on.
 *
 * To add a new item: prepend to `WHATS_NEW` with a fresh `id`.  Items the
 * user has already dismissed (per id) won't reappear.
 *
 * `audience` field on each item:
 *   - omitted / 'all'      → everyone (ScaleBoard-wide changes)
 *   - 'concierge'          → CEO + Concierge division (LC, CC, CS)
 *   - 'leaders'            → CEO + any division leader
 *   - 'ceo'                → CEO only
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Sparkles, X, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'sb_whats_new_dismissed';

// Most recent first.
const WHATS_NEW = [
  // ─── 8 May 2026 — Ghost-row killer (5× outlet inflation fixed) ───
  {
    id: 'ghost-row-killer-2026-05-08',
    date: '8 May 2026',
    title: 'Bundle 2.9.7-final-fix5 · Ghost-row killer · every outlet now mirrors eZee EXACTLY',
    body: 'Dipali spotted XIN Koramangala showing ₹15,145 / 3 orders on the dashboard tile for 6-May while the eZee API truth (and the WhatsApp pulse) showed ₹2,848 / 2 orders — a 5× ghost-row inflation. Same pattern across the other 4 outlets. Root cause: when eZee re-issues a record with a new record_id (amended bill, void + re-open, split bill), our upsert-by-dedupe_key created a NEW row instead of replacing the old one — both summed together. Fix: ingestion of both `ezee_orders` and `ezee_sales_records` now does `delete_many({date, source: "live"})` BEFORE upserting the fresh pull, so the collections always reflect the CURRENT eZee API state for that date. New CEO admin endpoint `POST /api/admin/wa-delivery/reingest-window?date_from=&date_to=` (max 32-day window) one-shot cleans historical drift on production. Verified on preview: re-ingest of 6-May deleted 135 ghost order rows + 112 ghost sales-records, and XIN Koramangala immediately corrected from ₹15,145 to ₹2,848. Diagnostic endpoint upgraded to report all three revenue surfaces (orders / sales_records / daily_summary) side-by-side for triangulation.',
    cta: 'Open MindEscapes',
    to: '/division/me',
    audience: 'ceo',
  },
  // ─── 7 May 2026 — Revenue source-of-truth doctrine locked ───
  {
    id: 'revenue-doctrine-2026-05-07',
    date: '7 May 2026',
    title: 'Bundle 2.9.7-final · Revenue doctrine locked · ONE source, ONE metric, ONE collection',
    body: 'After two days of pulse-vs-dashboard-vs-eZee-mobile drift, the canonical revenue doctrine is locked across ALL of ScaleBoard. (1) SOURCE = eZee API (NOT eZee mobile app — their app applies post-processing we can\'t reverse-engineer). (2) METRIC = `net_sales = gross_amount − flat_discount` (pre-tax, post-discount accounting revenue · the May-3 Celeste lock-truth ₹3,62,694). (3) COLLECTION = `ezee_sales_records` (the `/fas/get_sales_data` feed) is the SINGLE source for revenue totals. `ezee_orders` is for ITEM-LEVEL DETAIL only (top dishes, kitchen mix, peak hour) — NEVER aggregate revenue totals from it. (4) LABEL = "Net Revenue (excl. tax)" everywhere. (5) CRON LAG = 30-min cadence is normal POS behaviour; bills not yet closed in eZee aren\'t visible. The pulse, today_ticker, outlet intelligence tile, daily_sales mirror, and cost intel all now read from `ezee_sales_records`. New regression test `tests/test_revenue_doctrine.py` (2 contract tests) fails the build if any new file aggregates revenue from `ezee_orders` outside the explicit ALLOWED_DETAIL_FILES list.',
    cta: 'Open MindEscapes',
    to: '/division/me',
    audience: 'ceo',
  },
  // ─── 6 May 2026 — Outlet name canonicalisation + WhatsApp pulse delivery fix ───
  {
    id: 'outlet-canon-2026-05-06',
    date: '6 May 2026',
    title: 'Bundle 2.9.7 · Duplicate outlet rows AND silent WhatsApp drops · both killed at the source',
    body: 'Three structural fixes shipped together. (A) The duplicate-outlet-rows bug recurred for the third time today (Celeste By MindEscapes (Ketti) AND (KETTI), ANMA (Coonoor) AND (COONOOR), and so on). Root cause was structural: two display-name dictionaries had drifted from each other. Fix: `SCALEBOARD_TO_DAILY_SALES_OUTLET` is now the single source of truth, `COST_CENTRE_MAP[oid]["display_name"]` is locked to the same string, and an import-time `_assert_display_name_consistency()` check refuses to start the backend on any future drift. New `canonical_outlet_name(value)` helper used in the live ticker bucket as defence-in-depth. CEO admin endpoints `POST /api/admin/outlet-canonicalisation/audit` (read-only) + `apply?dry_run=true|false` clean up any legacy stale rows. New regression test `tests/test_outlet_canonicalisation.py` (26 tests). Verified live on production — live ticker shows exactly 6 outlet tiles, zero duplicates. (B) The 09:00 IST ME Outlet Pulse hadn\'t actually been landing on phones for the last two days — Gupshup returned `status: sent`, recipients got nothing. Two contributing fixes: (1) body rebuilt as PLAIN TEXT (no asterisks, no underscores) so Meta accepts it as an approved UTILITY template; (2) dispatch flipped back to template-FIRST. (3) Most importantly — the Gupshup webhook at `/api/mira-wa/webhook` was silently dropping every non-message event (delivery receipts, send-failures, read-receipts) with a `skipped: message-event` ack. The webhook now persists every non-message event to `wa_delivery_events` AND re-parses the body as form-encoded if `request.json()` fails (Gupshup posts DLRs as application/x-www-form-urlencoded with the JSON inside a `payload` form field). New CEO admin endpoint `GET /api/admin/wa-delivery/recent?phone=&hours=&include_raw=true` exposes everything including the full Gupshup → Meta failure payload. Production verification on this redeploy: 7 `enqueued` → 7 `failed` events captured for the test pulse to all 7 recipients, with the failures arriving ~250ms after enqueue (Meta-side rejection, not a Gupshup queue problem). The exact rejection reason will be visible in the next test send now that the form-parse fix is live. (C) Pre-existing tz-naive bug in `routes/ezee_health_routes.py` (lines 109 + 188) silently 500\'d the `/api/admin/ezee/sync-health` admin endpoint for weeks — fixed; sync-health is now GREEN on prod (last_run_age=148s, overall_verdict=green).',
    cta: 'Open MindEscapes',
    to: '/division/me',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Bundle 2.9.3 · Pipeline batch + ME ₹53L cleanup ───
  {
    id: 'pipeline-batch-2026-05-05',
    date: '5 May 2026',
    title: 'Pipeline batch · 13 new deals · MindEscapes ₹53L duplicate cleanup',
    body: 'Three big things landed today. (1) MindEscapes was over-reporting FY YTD by ₹53L because two manual weekly updates (Apr 7 + Apr 14) duplicated the eZee daily_sales feed. Fix: normalise both sides to Monday-of-week before deduping; cleanup endpoint deleted the 2 duplicates so MindEscapes now shows the same number on every surface (CEO card, Division Detail headline, per-outlet rows) — all reading from the eZee POS + PMS source of truth. (2) 13 new pipeline deals added in one batch: BMW (₹8.25Cr · CS B2B · 60%), KVB Phase 1 (₹1Cr · 90%) and Phase 2 (₹4Cr · 50%), Aratt 250 flats (₹75L · 50%), 9 LC deals (Brookfields Pune+Mumbai marked Won with auto-created contract + payment schedule, BT NCR, Brookfields Chennai, HDFC ERGO, HERO Fincorp, Magma, PACCAR, Anand Rathi, HDFC Bank, Emcure). (3) Payments received recorded as b2b_revenue: RedBeryl May ₹12L advance + TVS ₹5L ad hoc.',
    cta: 'Open Pipeline',
    to: '/pipeline',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Bundle 2.9 · Stays as 6th outlet pill + Sync Now ───
  {
    id: 'outlet-intel-bundle-2-9-2026-05-05',
    date: '5 May 2026',
    title: 'Stays joins Outlet Intel · Sync Now · CEO Dashboard auto-refreshes every 60 s',
    body: 'Bundle 2.9 brings four shipped fixes in one cycle. (1) Stays joins the Outlet Intelligence row as the 6th pill — same pill style, single row, same click behaviour. Stays-specific tabs: Revenue (Pure Room Revenue · Rule 16 · ADR · RevPAR), Occupancy (live room board), Channels (Direct vs Booking.com vs MakeMyTrip vs OTA + commission-drag estimate), Guest, Cost. (2) Critical bug fix: the 30-min intra-day eZee cron now also refreshes `ezee_orders` so the "TODAY REVENUE" tile reflects live data during the day, not only after midnight. (3) Sync Now button on the CEO Dashboard quick-action ribbon AND the Outlet Intel page header AND the LiveTicker — one click force-pulls eZee, polls until it lands, refreshes the parent. (4) CEO Dashboard now auto-refreshes every 60 seconds: division cards (MindEscapes, Les Concierges, Doggy Bakery, Club Social, CC Combined), Group Gap Screamer, Outlet Leaderboard, Revenue Trend, Division Contribution, Wins/Momentum, People Warnings — all remount as a single atomic group on every 60-s tick or Sync Now click. No more stale numbers while the live ticker is fresh.',
    cta: 'Open Outlet Intel · Stays',
    to: '/outlet-intelligence?outlet=stays_ketti&tab=revenue',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — ME Outlet Pulse cron (09:00 IST) ───
  {
    id: 'me-outlet-pulse-2026-05-05',
    date: '5 May 2026',
    title: 'ME Outlet Pulse · 09:00 IST WhatsApp to the operating seven',
    body: 'New daily WhatsApp at 09:00 IST to Dipali · Dhiraj · Saket · Ronald · Aditya · Nihal · Lohita. One compact message strings yesterday\'s revenue + leading dish per outlet (Celeste / ANMA Coonoor / ANMA Ooty / XIN HSR / XIN Kor / Stays) plus three pulse signals: peak-hour at the leading outlet, MTD food-cost % with health emoji, and live Stays occupancy. Footer hints at Mira drill-downs ("kitchen mix Celeste" / "food cost XIN HSR"). Reuses the approved `me_ops_brief` Gupshup template so it lands every day even outside the 24-hour session window. Idempotent — every run logged to `me_outlet_pulses`. Dry-run preview at `POST /api/admin/seed/run-outlet-pulse?dry_run=true`.',
    cta: 'Open Mira',
    to: '/operator-mira',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Bundle 2.8 · 9 new Mira intents (Menu / Cost / Stays) ───
  {
    id: 'mira-outlet-intel-bundle-2-8-2026-05-05',
    date: '5 May 2026',
    title: 'Mira now answers Menu / Cost / Stays questions on WhatsApp + Chat',
    body: 'Bundle 2.8 wires 9 new read-only intents that turn ScaleBoard\'s eZee POS + Absolute PMS data into natural-language answers — for both WhatsApp and the Web Chat panel. Try: "top dish at Celeste this month" / "kitchen mix for Celeste" / "channel mix at ANMA Coonoor" / "peak hour at XIN HSR yesterday" / "best server at Celeste" / "food cost at Celeste" / "Stays occupancy" / "daily sheet for XIN HSR". Dual-tone shipping: WhatsApp gets the crisp scan-while-walking format (*bold* headers, • bullets), Web Chat gets the conversational multi-paragraph narrative — same data, two renderings, single executor. DB-direct queries (no auth round-trip), real eZee field names, real range tokens (today/yesterday/7d/30d/mtd/ytd/last_month). All 9 verified on preview live data: Celeste BREAKFAST top at ₹33.1K, Stays at 80% occupancy ₹6.69L Pure Room Revenue (Rule 16), ANMA Coonoor 91.3% dine-in. 23/23 unit tests green.',
    cta: 'Open Mira',
    to: '/operator-mira',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Bundle 2.7 final pre-deploy audit ───
  {
    id: 'stays-pre-deploy-audit-2026-05-05',
    date: '5 May 2026',
    title: 'Stays — pre-deploy audit caught 4 more issues',
    body: 'Per Dipali\'s "find every issue before deploying" mandate: traced every read-path that touches daily_sales for ME revenue. Caught (1) outlet name mismatch — mirror wrote "Stays By MindEscapes (Ketti)" but registry has "Stays By MindEscapes" — fixed; (2) MonthlyArchive\'s CANONICAL_OUTLETS list didn\'t include stays_ketti — added with ezee_stays_folios source; (3) PUT/DELETE on /api/daily-sales would let users overwrite pms_live (and ezee_live) rows — now 403 with explicit "edit the source in eZee" message; (4) 4 ghost ₹0 manual Stays rows from the old CSV import that the date-by-date mirror missed — now swept across the entire backfill window. All 4 fixes verified on preview before save+deploy.',
    cta: 'Open ME Dashboard',
    to: '/division/me',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Stays daily_sales naming fix (Bundle 2.7) ───
  {
    id: 'stays-naming-fix-2026-05-05',
    date: '5 May 2026',
    title: 'Stays mirror — outlet name now matches registry',
    body: 'Bundle 2.6 fan-out worked but wrote daily_sales rows under "Stays By MindEscapes (Ketti)" while the outlet registry has "Stays By MindEscapes" (no suffix). Result: the leaderboard / portfolio / latest-metrics joins didn\'t connect — leaderboard showed Stays at ₹0. Fixed by aligning STAYS_OUTLET_NAME to the registered name. Backfill rerun will rename the existing 29 rows in place (upsert key uses outlet_id, not name).',
    cta: 'Open ME Dashboard',
    to: '/division/me',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Stays revenue now flows through ALL ME surfaces ───
  {
    id: 'stays-fanout-2026-05-05',
    date: '5 May 2026',
    title: 'Stays revenue now flows through every ME surface',
    body: 'Caught during prod verification: Stays integration was writing only to ezee_stays_folios, but the CEO Dashboard ME tile + ME YTD + Outlet Leaderboard + Revenue Trend + Mira / morning brief / midnight ping all read from daily_sales. Stays was visually present in its own tile but invisible to ME totals. Fixed by adding a daily_sales mirror — every backfill + nightly cron now writes one daily_sales row per checkout date (entered_by="pms_live"), so Stays revenue auto-flows to all 22+ surfaces. Same pattern as eZee POS. Auto-archives stale manual rows for Stays. ME YTD now correctly reflects rooms + restaurants combined per Rule 16.',
    cta: 'Open ME Dashboard',
    to: '/division/me',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Stays Intelligence live (eZee Absolute PMS) ───
  {
    id: 'stays-intelligence-live-2026-05-05',
    date: '5 May 2026',
    title: 'Stays Intelligence is live · MindEscapes Ketti',
    body: 'eZee Absolute PMS now flowing into ScaleBoard. Nightly sync at 23:50 IST captures every checked-out folio — and decodes it under Rule 16: Pure Room Revenue + non-F&B extras = the Stays headline; F&B charged to room (room service via folio) is recorded but excluded because it\'s already in Celeste POS. New tile on the ME page shows live occupancy, ADR, RevPAR, channel mix. Stays appears in the Dhiraj morning brief and the midnight ME WhatsApp ping. Apr 1 → today: 173 folios · ₹29.30L Stays revenue · ₹5.58L F&B-to-room (correctly excluded · Rule 16 receipt).',
    cta: 'Open ME Dashboard',
    to: '/division/me',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Rule 16 (Stays / POS revenue separation) ───
  {
    id: 'rule-16-stays-room-revenue-2026-05-05',
    date: '5 May 2026',
    title: 'Rule 16 — Stays revenue is Pure Room Revenue only',
    body: 'New canonical rule discovered during the PMS dry-run for MindEscapes: the eZee folio `total_amount` includes ~₹5.58L of room-service food charges over 35 days, ₹1.90L of which carry "Outlet: Celeste By MindEscapes" — meaning that same ₹1.90L is ALREADY in Celeste\'s POS daily_sales. To prevent double-counting F&B across POS and PMS, Stays headline now uses Pure Room Revenue (the "Room Revenue" reference_name in the folio detail array) — never the rolled-up total_amount. Non-F&B extras (Extra Bed, VIP, Cancellation, Channel fees) stay in. Saved before a single line of integration code was written.',
    cta: 'Open Documentation Hub',
    to: '/docs',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Session 2.4 hotfix (event-loop unblock for deck) ───
  {
    id: 'deck-event-loop-unblock-2026-05-05',
    date: '5 May 2026',
    title: 'JLR deck no longer freezes the rest of the app',
    body: 'The deeper root cause behind the 4-minute Cloudflare 520 outage: the LLM library we use (emergentintegrations) calls litellm.completion() synchronously under the hood — even though it\'s declared async. Each Claude roundtrip blocked FastAPI\'s event loop for 12-25 seconds, so 22 deck narratives = 4-9 minutes of total blocking, during which every /api/* endpoint timed out. Fixed by offloading the LLM call to a worker thread (asyncio.to_thread) so the main event loop stays responsive. Verified: 5 concurrent /api/health probes all succeed during live Claude narrative generation — pre-fix this would have starved.',
    cta: 'Open JLR Intelligence',
    to: '/client-intelligence/jlr',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Session 2.3 hotfix (production outage / kubelet liveness) ───
  {
    id: 'kubelet-liveness-2026-05-05',
    date: '5 May 2026',
    title: 'Production stability — Cloudflare 520 root-caused & fixed',
    body: 'Bundle 2.2 redeploy succeeded but a March 2026 deck round-trip caused a 4-minute Cloudflare 520 outage. Root cause: the Kubernetes liveness probe pings /health (bare path, not /api/health) every 10s. We only had /api/health, so /health returned 404. Under quiet load the 404 was instant; during the deck job\'s LLM saturation the 404 path queued behind work and exceeded nginx\'s 10s upstream timeout, so kubelet killed the pod after 3 missed probes. Fixed by adding bare-path /health, /healthz, /livez, /ready endpoints as SYNCHRONOUS routes (no awaits) so they\'re immune to event-loop saturation. Verified <6ms response time under load.',
    cta: 'Open Documentation Hub',
    to: '/docs',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Session 2.2 hotfix (deck orphan recovery + mobile palette) ───
  {
    id: 'mobile-monthly-archive-palette-2026-05-05',
    date: '5 May 2026',
    title: 'Monthly Archive now readable on mobile',
    body: 'The new Monthly Archive strip on the ME division page shipped with a dark-theme palette (bg-slate-900, text-slate-100) that rendered as a low-contrast grey wash on ScaleBoard\'s cream/navy light theme. Especially painful on mobile. Rewritten to the canonical palette — white cards, navy headline, cream sub-cards, #E5E1DC borders. Revenue figures and outlet names now clearly legible. Also handles missing dish names (shown as "— (unnamed)" in muted color instead of a blank row).',
    cta: 'Open ME Dashboard',
    to: '/division/me',
    audience: 'ceo',
  },
  {
    id: 'deck-orphan-recovery-2026-05-05',
    date: '5 May 2026',
    title: 'JLR deck — auto-recovers from backend restarts',
    body: 'Caught during the post-redeploy 21-check verification: any deck job in-flight when the backend restarts used to leave its DB row stuck in "queued"/"running" forever, permanently filling both concurrency slots and blocking new decks with a 429. Now: every backend boot auto-transitions stuck jobs to "failed (orphaned_by_restart)" via a startup hook, plus a new CEO admin endpoint POST /api/customer-intelligence/_admin/deck/recover-orphaned for on-demand recovery without a redeploy.',
    cta: 'Open JLR Intelligence',
    to: '/client-intelligence/jlr',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Session 2.1 hotfix bundle (CEO dashboard data trust) ───
  {
    id: 'data-trust-fixes-2026-05-05',
    date: '5 May 2026',
    title: 'CEO dashboard — 6 data-trust bugs fixed',
    body: 'JLR Members tile now reads canonical Primary+Active (was showing 3,090 raw). Soul-form completion uses the real CRM status field, not the legacy boolean (305 done · 931 pending, was inflated to 1,236/0). Contracted now reconciles between the chart caption and portfolio bar (was ₹14L apart). "Gap" relabelled "Uncovered Gap" with "Unrealised vs target" shown alongside. Friday Report now includes b2b_revenue for contract divisions so CC-Clubs/CS-B2B/LC stop showing 0%. CC parent renamed "CC (Combined)" with joint Nisha+Samar ownership. Stale-SOPs tile shows distinct documents (~466) with the per-person breakdown in the subtitle.',
    cta: 'Open CEO Dashboard',
    to: '/dashboard',
    audience: 'ceo',
  },
  // ─── 5 May 2026 — Session 2 bundle (data integrity + cost intel + deck path B) ───
  {
    id: 'deck-path-b-2026-05-05',
    date: '5 May 2026',
    title: 'JLR deck — async generation, no more 524s',
    body: 'Direct generate now returns immediately with a job id. Claude narratives + 27-slide assembly run in the background, the PPTX uploads to Cloudinary CDN, and Dipali + Nisha both get an email with the download link the moment it\'s ready. Concurrency capped at 2 jobs at a time — no more LLM rate-limit fights.',
    cta: 'Open JLR Intelligence',
    to: '/client-intelligence/jlr',
    audience: 'ceo',
  },
  {
    id: 'cost-intel-live-2026-05-05',
    date: '5 May 2026',
    title: 'Cost Intelligence — live eZee, no more CSVs',
    body: 'Net F&B cost is now wired to live eZee F&B purchases ÷ live eZee net sales. The "LIVE · MTD" pulsing badge on every outlet page tells you when the data is real-time. New 03:30 IST cron pulls yesterday\'s GRNs every night so Dhiraj never uploads a CSV again.',
    cta: 'Open Cost Intel',
    to: '/division/me',
    audience: 'leaders',
  },
  {
    id: 'monthly-archive-2026-05-05',
    date: '5 May 2026',
    title: 'Monthly Archive on the ME page',
    body: 'Below the dashboard, scroll down for month tabs. Click any month — see revenue per outlet, covers, top dishes, kitchen breakdown. YoY comparison lights up when we have 12-month prior data. The MTD/YTD/Q-progress tiles at the top stay unchanged.',
    cta: 'Open ME division',
    to: '/division/me',
    audience: 'leaders',
  },
  {
    id: 'sync-health-2026-05-05',
    date: '5 May 2026',
    title: 'Sync Health · single-truth monitoring',
    body: 'Every eZee cron now writes one row per fire to a ledger. The /api/admin/ezee/sync-health endpoint reads from that ledger — green/amber/red verdict per cron, last-run timestamp, drift between truth and mirror, and a live API reachability ping. No more guessing whether last night\'s sync fired.',
    audience: 'ceo',
  },
  {
    id: 'fy-anchor-fix-2026-05-05',
    date: '5 May 2026',
    title: 'Mira FY fix — every YTD now starts April 1',
    body: 'Mira\'s context builder was using calendar year (Jan 1) for YTD. Now anchored to Indian FY (Apr 1) — matching the CEO dashboard tile. Every YTD/Q1/contract calculation is consistent across surfaces. Locked by a regression test.',
    audience: 'ceo',
  },
  {
    id: 'dedup-157-rows-2026-05-05',
    date: '5 May 2026',
    title: 'Daily-sales cleanup — 157 duplicate rows archived',
    body: 'Where a manual entry duplicated an eZee live row for the same outlet + date, the manual one was archived to daily_sales_deleted (recoverable). ME YTD corrected from ₹2.31Cr to the true ₹1.34Cr. The unified mirror now auto-archives going forward — this can\'t recur.',
    audience: 'ceo',
  },
  {
    id: 'sync-auto-clear-2026-05-05',
    date: '5 May 2026',
    title: 'Stale sync banners auto-clear',
    body: 'When a fresh CRM/Desk sync succeeds for a client, any earlier failed run gets superseded automatically. No more red "sync failed 12 hr ago" banner shouting at you the morning after a deploy.',
    audience: 'concierge',
  },
  {
    id: 'latest-metrics-live-2026-05-05',
    date: '5 May 2026',
    title: 'Latest Metrics — live, not weekly form',
    body: 'The Latest Metrics widget on the ME page is now MTD per outlet from live eZee receipts. Look for the "LIVE · MTD" pulsing-green chip. Stays still uses the weekly form (PMS data not yet wired).',
    cta: 'Open ME division',
    to: '/division/me',
    audience: 'leaders',
  },
  {
    id: 'outlet-intel-nav-2026-05-05',
    date: '5 May 2026',
    title: 'Outlet Intel — now in the top nav',
    body: 'New nav item between Sales and Client Intel — opens the outlet drilldown directly (Stars / Plowhorses / Puzzles / Dogs Boston Matrix, Cost Intel tab, Menu Intel tab, Delivery Intel tab). One click instead of three.',
    cta: 'Open Outlet Intel',
    to: '/outlet-intelligence',
    audience: 'ceo',
  },
  {
    id: 'rule-15-no-docs-no-done-2026-05-05',
    date: '5 May 2026',
    title: 'Rule 15 — No docs, no done',
    body: 'Every deploy that ships user-visible functionality must update the docs (BUSINESS_MANUAL, this strip, complete-architecture, affected cheat guides) in the same bundle. The deploy-check K_documentation section enforces it — fails CI if any doc surface drifts. Stale docs become stale trust.',
    cta: 'Open System Docs',
    to: '/docs',
    audience: 'all',
  },
  // ─── 06 Feb 2026 — Workbench body fix + self-heal ────────────
  {
    id: 'workbench-body-heal-2026-02-06',
    date: '06 Feb 2026',
    title: 'Workbench drafts — empty body fix + one-click heal',
    body: 'Older drafts were rendering "No body" in the drawer because Claude\'s email block was being read from the wrong key. Fixed across every trigger (birthdays · dormant · soul · curated). An amber banner now detects legacy empty drafts on page load — click "Fix now · regenerate N" to re-run Claude in bulk. Per-draft "Regenerate with Claude" link inside every drawer too.',
    cta: 'Open Workbench',
    to: '/member-care/workbench',
    audience: 'concierge',
  },
  // ─── 02 May 2026 — Member Care Workbench + Deck Preview release ───
  {
    id: 'workbench-2026-05-02',
    date: '02 May 2026',
    title: 'Member Care Workbench — review, edit, push',
    body: 'MCE drafts no longer auto-push to Zoho at 3am. Open the Workbench every morning, scroll through grouped drafts (birthdays · dormant · soul · curated), edit any line, hit Send. Bulk push, skip with reason, CSV export, reset to Claude\'s original. Time-sensitive birthdays auto-push at 6pm if you don\'t get to them.',
    cta: 'Open Workbench',
    to: '/member-care/workbench',
    audience: 'concierge',
  },
  {
    id: 'workbench-suggest-2026-05-02',
    date: '02 May 2026',
    title: 'Suggested anchor for dormant members',
    body: 'On any dormant draft, click ✨ Suggest. Claude reads the member\'s last 5 inbound tickets + Soul Form and surfaces ONE specific anchor for the LSM — a venue, an experience, a date — instead of a generic check-in.',
    cta: 'Try it',
    to: '/member-care/workbench',
    audience: 'concierge',
  },
  {
    id: 'deck-preview-2026-05-02',
    date: '02 May 2026',
    title: 'Deck Preview — read, edit, then ship',
    body: 'Click "Generate Report" on the CI dashboard → "Preview & generate". Claude drafts all 27-29 narratives in 60s, you read each one, click "Rewrite with instruction" to ask for a shorter version or to mention a specific city, save → click Generate Final PPT.',
    cta: 'Open CI · JLR',
    to: '/client-intelligence/jlr',
    audience: 'concierge',
  },
  {
    id: 'proactive-care-deck-2026-05-02',
    date: '02 May 2026',
    title: 'Proactive Care section in the JLR deck',
    body: 'Two new slides ("The work behind the work" + breakdown grid) report MCE outreach as a first-class section in the client deck. Separate from engagement metrics but visible — clients now see the invisible work we do between their requests.',
    cta: 'Open CI · JLR',
    to: '/client-intelligence/jlr',
    audience: 'concierge',
  },
  {
    id: 'mce-engagement-fix-2026-05-02',
    date: '02 May 2026',
    title: 'Engagement metrics now exclude MCE + Soul Sheets',
    body: 'April JLR ticket count was 1,571 in Zoho — 980 were MCE outreach, 317 were Soul Sheet artefacts, only 274 real inbound requests. ScaleBoard now classifies these into 3 buckets so the client deck shows the honest 274. One-click backfill banner tags historical tickets.',
    cta: 'See the numbers',
    to: '/client-intelligence/jlr',
    audience: 'concierge',
  },
  // ─── 29 Apr 2026 — Cheat guides + Newsletter suite ────────────
  {
    id: 'cheat-guides-2026-04-29',
    date: '29 Apr 2026',
    title: 'Cheat Guides across 5 modules',
    body: 'Newsletters, Soul Sheets, Members, Member Care Engine, and Mira each have a "how to" panel at the top of their tab. Open by default — collapse anytime.',
    cta: 'Open the guide',
    to: '/guide',
    audience: 'all',
  },
  {
    id: 'newsletter-suite-2026-04-29',
    date: '29 Apr 2026',
    title: 'Newsletter Module — author, image-fill, send to Zoho',
    body: 'Claude auto-drafts the next month, "Auto-fill images" populates real venue photos in 30s, and "Bulk Handover" sends ONE email to Tanisha with the full HTML + CSV for Zoho Campaigns.',
    cta: 'Open Newsletters',
    to: '/client-intelligence/jlr',
    audience: 'concierge',
  },
  {
    id: 'duplicate-newsletter-2026-04-29',
    date: '29 Apr 2026',
    title: '"Duplicate to next month" — one-click clone',
    body: 'Open last month\'s newsletter → click "Duplicate to next month" → all content + images copy across → edit just the words.',
    cta: 'Try it',
    to: '/client-intelligence/jlr',
    audience: 'concierge',
  },
];

const CONCIERGE_DIVISIONS = new Set(['cc-clubs', 'cc-ops', 'lc', 'cs']);

function _can_see(item, user) {
  const aud = item.audience || 'all';
  if (aud === 'all') return true;
  if (!user) return false;
  if (user.role === 'ceo') return true;  // CEO sees everything
  if (aud === 'leaders') return ['leader', 'sub_leader'].includes(user.role);
  if (aud === 'concierge') return CONCIERGE_DIVISIONS.has((user.division_id || '').toLowerCase());
  if (aud === 'ceo') return false;
  return false;
}


export default function WhatsNewStrip() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  const visible = useMemo(
    () => WHATS_NEW.filter(x => !dismissed.has(x.id) && _can_see(x, user)).slice(0, 3),
    [dismissed, user],
  );

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next))); } catch {}
  };

  const dismissAll = () => {
    const next = new Set([...dismissed, ...WHATS_NEW.map(x => x.id)]);
    setDismissed(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next))); } catch {}
  };

  if (visible.length === 0) return null;

  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ borderColor: '#E2D8C2', background: 'linear-gradient(180deg, #FFF8EC 0%, #FFFAF0 100%)' }}
      data-testid="whats-new-strip">
      <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid #F0E5C8' }}>
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: '#7A5C2B' }} />
          <span className="text-[12px] font-semibold tracking-wider uppercase" style={{ color: '#7A5C2B' }}>
            What's New
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: '#7A5C2B', color: '#FFFFFF' }}>
            {visible.length} {visible.length === 1 ? 'update' : 'updates'}
          </span>
        </div>
        <button onClick={dismissAll}
          className="text-[11px] hover:underline"
          style={{ color: '#9C7A3F' }}
          data-testid="whats-new-dismiss-all">
          Dismiss all
        </button>
      </div>

      <div className="divide-y" style={{ borderColor: '#F0E5C8' }}>
        {visible.map(item => (
          <div key={item.id}
            className="flex items-start justify-between gap-3 px-4 py-2.5"
            data-testid={`whats-new-item-${item.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12.5px] font-semibold" style={{ color: '#1B2A4A' }}>
                  {item.title}
                </span>
                <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{item.date}</span>
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: '#5C4F3F' }}>
                {item.body}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {item.to && (
                <button onClick={() => navigate(item.to)}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-white"
                  style={{ borderColor: '#7A5C2B', color: '#7A5C2B' }}
                  data-testid={`whats-new-cta-${item.id}`}>
                  {item.cta || 'Tour me'} <ArrowRight size={11} />
                </button>
              )}
              <button onClick={() => dismiss(item.id)}
                className="p-1 rounded hover:bg-white"
                title="Dismiss"
                style={{ color: '#9CA3AF' }}
                data-testid={`whats-new-dismiss-${item.id}`}>
                <X size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
